import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { HR } from '../components/HR'
import { Button } from '../components/Button'
import { Tag } from '../components/Tag'
import { api, type ApiEvent } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { isSafeUrl } from '../utils/url'
import { formatEventDateParts } from '../utils/eventDate'
import styles from './EventDetailScreen.module.css'

export function EventDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [event, setEvent] = useState<ApiEvent | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [editData, setEditData] = useState<{ title: string; description: string; price: string; venue_name: string; kind?: string; genre?: string; ticketing_mode?: string; ticket_url?: string } | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const { user } = useAuth()

  // Fetch event data from backend
  useEffect(() => {
    if (id) {
      api.events.get(Number(id))
        .then(setEvent)
        .catch(() => setLoading(false))
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [id])

  const handleSaveEdit = async () => {
    if (!event || !editData) return
    try {
      const updated = await api.events.update(Number(id), {
        title: editData.title,
        description: editData.description,
        price: editData.price,
        venue_name: editData.venue_name,
        kind: editData.kind,
        genre: editData.genre,
        ticketing_mode: editData.ticketing_mode,
        ticket_url: editData.ticket_url,
      })
      setEvent({ ...event, ...updated })
      setEditing(false)
      setEditData(null)
      setActionError(null)
    } catch (err) {
      setActionError('Failed to update event. Please try again.')
    }
  }

  const handleCancelEdit = () => {
    setEditing(false)
    setEditData(null)
    setActionError(null)
  }

  const startEditing = () => {
    if (event) {
      setEditData({
        title: event.title,
        description: event.description,
        price: event.price,
        venue_name: event.venue_name,
        kind: event.kind,
        genre: event.genre,
        ticketing_mode: event.ticketing_mode,
        ticket_url: event.ticket_url,
      })
      setEditing(true)
    }
  }

  if (loading) {
    return (
      <div className={styles.loading}>
        Loading...
      </div>
    )
  }

  if (!event) {
    return (
      <div className={styles.notFound}>
        Event not found
      </div>
    )
  }

  // Format date
  const date = new Date(event.event_date)
  const { dayName, dayNum, time } = formatEventDateParts(date)

  return (
    <div>
      {/* Back button */}
      <div className={styles.backButtonContainer}>
        <button
          onClick={() => navigate(-1)}
          className={styles.backButton}
        >
          ← Back
        </button>
      </div>

      {/* Event header */}
      <div className={styles.header}>
        <div className={styles.dateInfo}>
          <div className={styles.dateName}>{dayName}</div>
          <div className={styles.dateNumber}>{dayNum}</div>
          <div className={styles.dateTime}>{time}</div>
        </div>
        <div className={styles.eventInfo}>
          {editing ? (
            <div>
              <input
                type="text"
                value={editData?.title || ''}
                onChange={e => setEditData({ ...editData!, title: e.target.value })}
                className={styles.editInput}
              />
              <input
                type="text"
                value={editData?.venue_name || ''}
                onChange={e => setEditData({ ...editData!, venue_name: e.target.value })}
                className={styles.editVenueInput}
              />
              <input
                type="text"
                value={editData?.price || ''}
                onChange={e => setEditData({ ...editData!, price: e.target.value })}
                className={styles.editVenueInput}
                placeholder="Price"
              />
              <div className={styles.eventTags}>
                <select
                  value={editData?.kind || ''}
                  onChange={e => setEditData({ ...editData!, kind: e.target.value })}
                  className={styles.editVenueInput}
                >
                  <option value="music">Music</option>
                  <option value="market">Market</option>
                  <option value="community">Community</option>
                  <option value="spoken_word">Spoken Word</option>
                  <option value="film">Film</option>
                  <option value="workshop">Workshop</option>
                  <option value="other">Other</option>
                </select>
                <input
                  type="text"
                  value={editData?.genre || ''}
                  onChange={e => setEditData({ ...editData!, genre: e.target.value })}
                  className={styles.editVenueInput}
                  placeholder="Genre"
                />
              </div>
              <div className={styles.eventTags}>
                <select
                  value={editData?.ticketing_mode || 'sell'}
                  onChange={e => setEditData({ ...editData!, ticketing_mode: e.target.value })}
                  className={styles.editVenueInput}
                >
                  <option value="sell">Sell (integrated)</option>
                  <option value="external">External link</option>
                  <option value="free">Free / RSVP</option>
                </select>
                {editData?.ticketing_mode === 'external' && (
                  <input
                    type="url"
                    value={editData?.ticket_url || ''}
                    onChange={e => setEditData({ ...editData!, ticket_url: e.target.value })}
                    className={styles.editVenueInput}
                    placeholder="https://..."
                  />
                )}
              </div>
            </div>
          ) : (
            <>
              <h2 className={styles.eventTitle}>{event.title}</h2>
              <div className={styles.eventVenue}>
                {event.venue_name}
                {event.venue_address && ` · ${event.venue_address}`}
              </div>
              <div className={styles.eventTags}>
                <Tag variant="neutral">{event.kind}</Tag>
                {event.genre && <Tag variant="accent">{event.genre}</Tag>}
                <Tag variant="outline">{event.price}</Tag>
              </div>
            </>
          )}
          {user && user.role === 'admin' && !editing && (
            <button
              onClick={startEditing}
              className={styles.editButton}
            >
              Edit
            </button>
          )}
          {editing && (
            <div className={styles.editButtons}>
              <button
                onClick={handleSaveEdit}
                className={styles.saveButton}
              >
                Save
              </button>
              <button
                onClick={handleCancelEdit}
                className={styles.cancelButton}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      <HR />

      {/* Description */}
      {editing ? (
        <div className={styles.editSection}>
          <textarea
            value={editData?.description || ''}
            onChange={e => setEditData({ ...editData!, description: e.target.value })}
            className={styles.editDescription}
          />
        </div>
      ) : (
        event.description && (
          <div className={styles.description}>
            <p className={styles.descriptionText}>{event.description}</p>
          </div>
        )
      )}

      <HR />

      {/* Details */}
      <div className={styles.detailsSection}>
        <div className={styles.detailsHeader}>Details</div>
        <div className={styles.detailsGrid}>
          <DetailRow label="Date" value={date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} />
          <DetailRow label="Time" value={time} />
          <DetailRow label="Venue" value={event.venue_name} />
          {event.age_restriction && <DetailRow label="Age" value={event.age_restriction} />}
          {event.bar_policy && <DetailRow label="Bar" value={event.bar_policy} />}
          {event.accessibility_notes && <DetailRow label="Accessibility" value={event.accessibility_notes} />}
          {event.needs_volunteers && <DetailRow label="Volunteers needed" value={`${event.needs_volunteers} spots`} />}
        </div>
      </div>

      <HR />

      {/* Host */}
      {event.host_name && (
        <div className={styles.hostSection}>
          <div className={styles.hostHeader}>Hosted by</div>
          <div className={styles.hostName}>{event.host_name}</div>
        </div>
      )}

      {/* Organiser */}
      {event.organiser_name && (
        <div className={styles.organiserSection}>
          <div className={styles.organiserHeader}>Presented by</div>
          <div className={styles.organiserName}>{event.organiser_name}</div>
        </div>
      )}

      <HR />

      {/* Action */}
      <div className={styles.actionSection}>
        {event.ticketing_mode === 'external' && event.ticket_url && isSafeUrl(event.ticket_url) ? (
          <a href={event.ticket_url} target="_blank" rel="noopener noreferrer" className={`${styles.actionButton} btn-block`}>
            Get Tickets
          </a>
        ) : (
          <>
            <Button className={`${styles.actionButton} btn-block`} onClick={() => navigate(`/event/${id}/tickets`)}>
              Get Tickets
            </Button>
            <p className={styles.actionText}>
              You'll finish on Ticket Tailor's secure page.
            </p>
          </>
        )}
      </div>

      <HR />

      {/* Report button */}
      <div className={styles.actionSection}>
        <Button className={`${styles.reportButton} btn-block`} onClick={() => navigate(`/event/${id}/incident`)}>
          Report an issue
        </Button>
        <p className={styles.actionText}>
          Confidential — visible only to trusted organisers.
        </p>
      </div>

      {/* Volunteers button */}
      {event.needs_volunteers && (
        <div className={styles.actionSection}>
          <Button className={`${styles.volunteerButton} btn-block`} onClick={() => navigate(`/event/${id}/volunteers`)}>
            Manage volunteers
          </Button>
          <p className={styles.actionText}>
            Sign up or coordinate volunteers for this event.
          </p>
        </div>
      )}

      {/* Door check-in */}
      <div className={styles.actionSection}>
        <Button className={`${styles.actionButton} btn-block`} onClick={() => navigate(`/event/${id}/checkins`)}>
          Door check-in
        </Button>
        <p className={styles.actionText}>
          Track arrivals and departures at the door.
        </p>
      </div>

      {/* Curated indicator */}
      {event.is_curated && (
        <div className={styles.curatedTag}>
          <Tag variant="neutral">Under curation</Tag>
        </div>
      )}

      {/* Action error banner */}
      {actionError && (
        <div className={styles.errorBanner}>
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className={styles.errorBannerButton}>×</button>
        </div>
      )}
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.detailRow}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{value}</span>
    </div>
  )
}
